import React, { useEffect, useMemo, useState } from "react";
import { mcpApi } from "../aopApi";

// The one server the dashboard can talk to live, via the JWT-authed Express
// bridge (/api/v1/mcp/*). Everything else in the catalog is documentation —
// stdio processes and OAuth connectors a shared web app cannot reach.
const LIVE_SERVER_ID = "csd-mcp";

// Convert an MCP JSON Schema inputSchema to the page's row format.
function liveToolToCatalog(tool) {
	const props = (tool.inputSchema && tool.inputSchema.properties) || {};
	const required = (tool.inputSchema && tool.inputSchema.required) || [];
	const inputSchema = {};
	Object.entries(props).forEach(([key, value]) => {
		inputSchema[key] = {
			type: value.type || "string",
			description: value.description || "",
			required: required.includes(key)
		};
	});
	return {
		name: tool.name,
		description: tool.description,
		inputSchema,
		outputShape: "Live server — execute the tool below to see the real response.",
		readOnly: tool.readOnly,
		live: true
	};
}

function prettyMaybeJson(text) {
	try {
		return JSON.stringify(JSON.parse(text), null, 2);
	} catch {
		return text;
	}
}

function catalogTools(names) {
	return names.map((name) => ({
		name,
		description: "Representative tool exposed by this MCP server.",
		inputSchema: {},
		outputShape: "{ result }"
	}));
}

const BASE_MCP_SERVERS = [
	{
		id: "csd-mcp",
		name: "CSD Internal",
		icon: "⬡",
		status: "disconnected",
		transport: "stdio / Streamable HTTP",
		description:
			"Internal docs and CSD company/form tools, served by this codebase. Status, schemas, and test execution below are live via the dashboard bridge when the mcp:http process is running; write tools (create_account, edit_company, create_csd, edit_csd) are blocked unless the bridge enables writes.",
		tools: [
			{
				name: "search_internal_docs",
				description:
					"Full-text search across internal documentation. Returns ranked snippets with doc metadata.",
				inputSchema: {
					query: { type: "string", description: "Search query", required: true },
					limit: { type: "number", description: "Max results (default 5, max 20)", required: false }
				},
				outputShape: "{ docs: [{ id, title, snippet, score }] }"
			},
			{
				name: "get_internal_doc",
				description: "Retrieve a specific internal doc by ID. Content is truncated and secret-redacted.",
				inputSchema: {
					id: { type: "string", description: "Document ID", required: true }
				},
				outputShape: "{ id, title, content, updatedAt }"
			},
			{
				name: "search_companies",
				description: "Search CSD company records by name, domain, or Salesforce ID.",
				inputSchema: {
					query: { type: "string", description: "Company name or domain fragment", required: true },
					limit: { type: "number", description: "Max results", required: false }
				},
				outputShape: "{ companies: [{ id, name, domain, sfId }] }"
			},
			{
				name: "get_company",
				description: "Retrieve a company record by ID with whitelisted fields.",
				inputSchema: {
					id: { type: "string", description: "Company record ID", required: true }
				},
				outputShape: "{ id, name, domain, sfId, status, createdAt }"
			},
			{
				name: "edit_company",
				description: "Edit an existing company record.",
				inputSchema: {
					id: { type: "string", description: "Company record ID", required: true }
				},
				outputShape: "{ updated, id }"
			},
			{
				name: "search_forms",
				description: "Search CSD form records by company or form name.",
				inputSchema: {
					query: { type: "string", description: "Search string", required: true },
					companyId: { type: "string", description: "Scope to company", required: false }
				},
				outputShape: "{ forms: [{ id, name, companyId, type }] }"
			},
			{
				name: "get_form",
				description: "Retrieve a form record by ID.",
				inputSchema: {
					id: { type: "string", description: "Form record ID", required: true }
				},
				outputShape: "{ id, name, companyId, type, config }"
			},
			{
				name: "edit_csd",
				description: "Edit an existing CSD/form record.",
				inputSchema: {
					id: { type: "string", description: "Form record ID", required: true }
				},
				outputShape: "{ updated, id }"
			}
		]
	},
	{
		id: "figma",
		name: "Figma",
		icon: "◈",
		status: "connected",
		transport: "HTTP/SSE",
		description: "Design-to-code and code-to-design bridge. Read and write Figma files via the Plugin API.",
		tools: [
			{
				name: "get_design_context",
				description: "Fetch design tokens, component specs, and reference code for a Figma node.",
				inputSchema: {
					nodeId: { type: "string", required: true },
					fileKey: { type: "string", required: true }
				},
				outputShape: "{ code, screenshot, metadata }"
			},
			{
				name: "get_screenshot",
				description: "Render a node as PNG and return a short-lived URL.",
				inputSchema: {
					nodeId: { type: "string", required: true },
					fileKey: { type: "string", required: true },
					maxDimension: { type: "number", required: false }
				},
				outputShape: "{ image_url, width, height }"
			},
			{
				name: "use_figma",
				description: "Execute Plugin API JavaScript in a Figma file.",
				inputSchema: { code: { type: "string", required: true }, fileKey: { type: "string", required: true } },
				outputShape: "{ result }"
			},
			{
				name: "get_libraries",
				description: "List available design system libraries.",
				inputSchema: {},
				outputShape: "{ libraries[] }"
			},
			{
				name: "search_design_system",
				description: "Search for components in the design system by keyword.",
				inputSchema: { query: { type: "string", required: true } },
				outputShape: "{ components[] }"
			}
		]
	},
	{
		id: "youtrack",
		name: "YouTrack",
		icon: "◉",
		status: "connected",
		transport: "HTTP",
		description: "Issue tracking integration. Create, update, and query YouTrack issues and articles.",
		tools: [
			{
				name: "search_issues",
				description: "Search issues with a YouTrack query string.",
				inputSchema: { query: { type: "string", required: true }, limit: { type: "number", required: false } },
				outputShape: "{ issues[] }"
			},
			{
				name: "get_issue",
				description: "Get a specific issue by ID.",
				inputSchema: { id: { type: "string", required: true } },
				outputShape: "{ id, summary, description, status, assignee }"
			},
			{
				name: "create_issue",
				description: "Create a new issue in a project.",
				inputSchema: {
					project: { type: "string", required: true },
					summary: { type: "string", required: true },
					description: { type: "string", required: false }
				},
				outputShape: "{ id, url }"
			},
			{
				name: "update_issue",
				description: "Update fields on an existing issue.",
				inputSchema: { id: { type: "string", required: true } },
				outputShape: "{ success }"
			},
			{
				name: "add_issue_comment",
				description: "Add a comment to an issue.",
				inputSchema: { id: { type: "string", required: true }, text: { type: "string", required: true } },
				outputShape: "{ commentId }"
			}
		]
	},
	{
		id: "google-drive",
		name: "Google Drive",
		icon: "▲",
		status: "auth-required",
		transport: "OAuth/HTTP",
		description: "Google Drive integration for reading and writing documents.",
		tools: [
			{
				name: "authenticate",
				description: "Initiate OAuth flow for Google Drive.",
				inputSchema: {},
				outputShape: "{ authUrl }"
			},
			{
				name: "complete_authentication",
				description: "Complete OAuth after redirect.",
				inputSchema: { code: { type: "string", required: true } },
				outputShape: "{ success }"
			}
		]
	}
];

const EXTRA_MCP_SERVERS = [
	{
		id: "salesforce-sobject-reads",
		name: "Salesforce sObject Reads",
		icon: "◍",
		status: "auth-required",
		transport: "OAuth/HTTP",
		description: "Hosted Salesforce MCP for read-only platform and sObject access.",
		tools: catalogTools(["sobject_describe", "sobject_query", "record_get"])
	},
	{
		id: "salesforce-dx",
		name: "Salesforce DX",
		icon: "◌",
		status: "auth-required",
		transport: "stdio",
		description: "Local Salesforce CLI MCP using authorized sf org aliases and selected toolsets.",
		tools: catalogTools(["org_list", "data_query", "apex_run"])
	},
	{
		id: "gitea",
		name: "Gitea",
		icon: "◆",
		status: "auth-required",
		transport: "stdio",
		description: "Git server integration for repositories hosted on git.upsellit.com.",
		tools: catalogTools(["repo_search", "pull_request_get", "commit_read"])
	},
	{
		id: "github",
		name: "GitHub",
		icon: "●",
		status: "auth-required",
		transport: "HTTP / Docker stdio",
		description: "GitHub MCP for hosted remote access or local Docker-backed repository operations.",
		tools: catalogTools(["search_repositories", "pull_request_read", "issue_read"])
	},
	{
		id: "slack",
		name: "Slack",
		icon: "▣",
		status: "auth-required",
		transport: "Partner OAuth",
		description: "Slack MCP for workspace messages and approval-related communication surfaces.",
		tools: catalogTools(["search_messages", "channel_read", "message_draft"])
	},
	{
		id: "dropbox",
		name: "Dropbox",
		icon: "◫",
		status: "auth-required",
		transport: "OAuth/HTTP",
		description: "Dropbox MCP connector for file search, reads, and document context.",
		tools: catalogTools(["file_search", "file_get", "folder_list"])
	},
	{
		id: "google-analytics",
		name: "Google Analytics",
		icon: "△",
		status: "auth-required",
		transport: "stdio",
		description: "Google Analytics MCP using service account credentials for reporting queries.",
		tools: catalogTools(["property_list", "run_report", "metadata_get"])
	},
	{
		id: "shopify",
		name: "Shopify Dev",
		icon: "▰",
		status: "connected",
		transport: "stdio",
		description: "Shopify developer MCP and AI toolkit for storefront app work.",
		tools: catalogTools(["storefront_docs", "theme_context", "app_guidance"])
	},
	// {
	//   id: 'email-on-acid',
	//   name: 'Email on Acid',
	//   icon: '✉',
	//   status: 'auth-required',
	//   transport: 'Pipedream HTTP',
	//   description: 'Email QA MCP surface through Pipedream for campaign testing workflows.',
	//   tools: catalogTools(['email_test_create', 'render_preview', 'result_read']),
	// },
	{
		id: "rippling",
		name: "Rippling",
		icon: "◇",
		status: "auth-required",
		transport: "stdio / hosted gateway",
		description: "HR and employee system MCP through Rippling or a hosted gateway.",
		tools: catalogTools(["employee_search", "user_get", "org_context"])
	},
	{
		id: "quickbooks",
		name: "QuickBooks",
		icon: "$",
		status: "auth-required",
		transport: "stdio",
		description: "QuickBooks Online MCP for finance context through Intuit OAuth credentials.",
		tools: catalogTools(["company_info", "invoice_search", "customer_get"])
	},
	{
		id: "aws",
		name: "AWS",
		icon: "☁",
		status: "auth-required",
		transport: "stdio",
		description: "AWS Labs MCP servers for API, documentation, and pricing lookups.",
		tools: catalogTools(["aws_api", "aws_docs", "aws_pricing"])
	},
	{
		id: "wingman",
		name: "Wingman",
		icon: "⌁",
		status: "auth-required",
		transport: "HTTP",
		description: "Local Wingman MCP endpoint for local automation workflows.",
		tools: catalogTools(["wingman_context", "local_action", "status_check"])
	},
	{
		id: "linode",
		name: "Linode",
		icon: "▵",
		status: "auth-required",
		transport: "stdio",
		description: "Linode MCP for cloud infrastructure context and account operations.",
		tools: catalogTools(["instances_list", "domain_list", "token_check"])
	},
	{
		id: "zoom",
		name: "Zoom",
		icon: "◎",
		status: "auth-required",
		transport: "OAuth/HTTP",
		description: "Zoom MCP or ChatGPT plugin reference for meeting and webinar workflows.",
		tools: catalogTools(["meeting_search", "meeting_get", "recording_read"])
	},
	{
		id: "salesloft",
		name: "Salesloft",
		icon: "◐",
		status: "auth-required",
		transport: "OAuth/HTTP",
		description: "Salesloft MCP for sales engagement context and cadence workflows.",
		tools: catalogTools(["cadence_search", "person_get", "activity_read"])
	},
	{
		id: "linkedin",
		name: "LinkedIn",
		icon: "in",
		status: "auth-required",
		transport: "community stdio",
		description: "Community LinkedIn MCP server for LinkedIn research and profile context.",
		tools: catalogTools(["profile_search", "company_get", "post_read"])
	}
	// {
	//   id: 'adobe-express',
	//   name: 'Adobe Express',
	//   icon: '✦',
	//   status: 'auth-required',
	//   transport: 'local development',
	//   description: 'Adobe Express add-on local MCP development workflow.',
	//   tools: catalogTools(['addon_context', 'asset_read', 'local_preview']),
	// },
	// {
	//   id: 'excalidraw',
	//   name: 'Excalidraw',
	//   icon: '□',
	//   status: 'auth-required',
	//   transport: 'HTTP',
	//   description: 'Excalidraw Plus MCP for diagrams and collaborative drawings.',
	//   tools: catalogTools(['drawing_search', 'drawing_get', 'scene_export']),
	// },
];

const INSTALL_GUIDES = {
	"csd-mcp": {
		category: "Existing Upsellit MCP",
		auth: "OAuth prompt. Use the internal Upselly MCP password when prompted; do not commit the password to source.",
		chatgptWeb: [
			"Settings -> Connectors -> Create",
			"Name it Upselly MCP or CSD MCP.",
			"Connector URL: https://docs.upsellit.com/mcp",
			"Complete the OAuth prompt with the internal password."
		],
		claudeApp: [
			"Add as a remote HTTP MCP from Claude Desktop or Claude app connector settings when available.",
			"Endpoint: https://docs.upsellit.com/mcp",
			"Complete OAuth with the internal password."
		],
		claudeCli: "claude mcp add --transport http usi-docs-mcp https://docs.upsellit.com/mcp",
		alternate:
			'claude mcp add --transport http --header "Authorization: Bearer YOUR_TOKEN" csd-mcp https://docs.upsellit.com/mcp',
		references: ["https://docs.upsellit.com/mcp", "https://modelcontextprotocol.io/docs/tools/inspector"]
	},
	figma: {
		category: "Design MCP",
		auth: "Figma login/OAuth from the Figma account and Dev Mode MCP setup.",
		chatgptWeb: [
			"Use the Figma connector/plugin where available.",
			"Authorize with the Figma account that has file access."
		],
		claudeApp: [
			"Use Figma Dev Mode MCP setup from the Figma UI or supported IDE integration.",
			"Keep file access scoped to the relevant team/project."
		],
		claudeCli: "Use Figma Dev Mode MCP setup from Figma; command varies by Figma client and plan.",
		alternate: "Use the official Figma MCP integration in supported IDEs.",
		references: ["https://github.com/openai/plugins/tree/main/plugins/figma"]
	},
	youtrack: {
		category: "Project Tracking MCP",
		auth: "YouTrack permanent token. Profile -> Account Security -> Tokens -> New token with YouTrack scope.",
		chatgptWeb: [
			"Create a custom connector if your ChatGPT workspace supports remote MCP.",
			"Endpoint: https://upsellit.youtrack.cloud/mcp",
			"Use Authorization: Bearer YOUR_TOKEN when headers are supported."
		],
		claudeApp: [
			"Add the remote HTTP MCP endpoint from Claude Desktop settings.",
			"Header: Authorization: Bearer YOUR_TOKEN"
		],
		claudeCli:
			'claude mcp add --header "Authorization: Bearer YOUR_TOKEN" --transport http youtrack https://upsellit.youtrack.cloud/mcp',
		alternate:
			"claude mcp add youtrack-local -- npx mcp-remote https://upsellit.youtrack.cloud/mcp --header Authorization:${AUTH_HEADER}",
		references: [
			"https://www.jetbrains.com/help/youtrack/server/model-context-protocol-server.html#local-mcp-client"
		]
	},
	"google-drive": {
		category: "Document MCP",
		auth: "Google OAuth during connection.",
		chatgptWeb: [
			"Use the Google Drive connector/plugin from ChatGPT connectors where available.",
			"Authorize the Google account containing the target files."
		],
		claudeApp: ["Use Claude connector settings where Google Drive MCP is available.", "Complete Google OAuth."],
		claudeCli: "Use a Google Drive MCP server or plugin configuration approved for the workspace.",
		alternate: "OpenAI plugin reference: https://github.com/openai/plugins/tree/main/plugins/google-drive",
		references: ["https://github.com/openai/plugins/tree/main/plugins/google-drive"]
	},
	"salesforce-sobject-reads": {
		category: "CRM MCP",
		auth: "OAuth through Salesforce connected app or External Client App flow.",
		chatgptWeb: [
			"Use a remote MCP connector if available for your workspace.",
			"Endpoint: https://api.salesforce.com/platform/mcp/v1/platform/sobject-reads",
			"Complete Salesforce OAuth."
		],
		claudeApp: ["Add remote HTTP MCP endpoint and complete Salesforce OAuth."],
		claudeCli:
			"claude mcp add --transport http salesforce-sobject-reads https://api.salesforce.com/platform/mcp/v1/platform/sobject-reads",
		alternate:
			"claude mcp add --transport http salesforce-sobject-reads https://api.salesforce.com/platform/mcp/v1/sandbox/platform/sobject-reads",
		references: ["https://github.com/salesforcecli/mcp"]
	},
	"salesforce-dx": {
		category: "CRM MCP",
		auth: "Uses locally authorized Salesforce CLI orgs. Run sf org login web first.",
		chatgptWeb: [
			"Not recommended for web-only clients because it depends on local Salesforce CLI auth.",
			"Use hosted sObject Reads MCP for web connectors."
		],
		claudeApp: ["Configure as a local stdio MCP server if the app supports local MCP config."],
		claudeCli:
			"claude mcp add salesforce-dx -- npx -y @salesforce/mcp --orgs DEFAULT_TARGET_ORG --toolsets core,data",
		alternate: "claude mcp add salesforce-dx -- npx -y @salesforce/mcp --orgs YOUR_ORG_ALIAS --toolsets core,data",
		references: ["https://github.com/salesforcecli/mcp"]
	},
	gitea: {
		category: "Git MCP",
		auth: "Gitea Personal Access Token from https://git.upsellit.com -> User Settings -> Applications.",
		chatgptWeb: ["Use through a hosted MCP bridge only; local npx stdio does not work from web-only clients."],
		claudeApp: [
			"Configure as a local stdio MCP server with GITEA_HOST and GITEA_ACCESS_TOKEN environment variables."
		],
		claudeCli:
			"claude mcp add gitea --env GITEA_HOST=https://git.upsellit.com --env GITEA_ACCESS_TOKEN=YOUR_TOKEN -- npx -y gitea-mcp",
		alternate: "Install the gitea-mcp binary locally and run it instead of npx.",
		references: ["https://gitea.com/gitea/gitea-mcp"]
	},
	github: {
		category: "Git MCP",
		auth: "GitHub PAT for local Docker, or OAuth for hosted remote MCP.",
		chatgptWeb: [
			"Use GitHub connector/plugin where available and authorize with OAuth.",
			"OpenAI plugin references are linked below."
		],
		claudeApp: ["Use remote GitHub MCP if available, or local Docker stdio configuration."],
		claudeCli: "claude mcp add --transport http github https://api.githubcopilot.com/mcp/",
		alternate:
			"claude mcp add github --env GITHUB_PERSONAL_ACCESS_TOKEN=YOUR_GITHUB_PAT -- docker run -i --rm -e GITHUB_PERSONAL_ACCESS_TOKEN ghcr.io/github/github-mcp-server",
		references: ["https://github.com/github/github-mcp-server"]
	},
	slack: {
		category: "Communication MCP",
		auth: "Partner OAuth through Slack supported setup.",
		chatgptWeb: [
			"Use ChatGPT Slack connector/plugin where approved by the workspace.",
			"Authorize through Slack OAuth."
		],
		claudeApp: ["Connect Slack from Claude integration/connector UI where available."],
		claudeCli: "No stable public claude mcp add command is listed by Slack; use supported partner setup.",
		alternate: "OpenAI Slack plugin reference is linked below.",
		references: [
			"https://slack.com/help/articles/48855576908307-Guide-to-the-Slack-MCP-server",
			"https://github.com/openai/plugins/blob/main/plugins/slack/.codex-plugin/plugin.json"
		]
	},
	dropbox: {
		category: "Document MCP",
		auth: "Dropbox OAuth during connection.",
		chatgptWeb: [
			"Create a remote MCP connector with endpoint https://mcp.dropbox.com/mcp where supported.",
			"Complete Dropbox sign-in."
		],
		claudeApp: ["Add Dropbox remote MCP endpoint and complete OAuth."],
		claudeCli: "claude mcp add --transport http dropbox https://mcp.dropbox.com/mcp",
		alternate: "Use Cursor, Claude, or ChatGPT connector setup with the same endpoint.",
		references: ["https://help.dropbox.com/integrations/connect-dropbox-mcp-server"]
	},
	"google-analytics": {
		category: "Analytics MCP",
		auth: "Google service account credentials JSON plus project ID.",
		chatgptWeb: [
			"Use through a hosted MCP bridge only; local service-account files are not available to web-only clients."
		],
		claudeApp: ["Configure as a local stdio MCP server with GOOGLE_APPLICATION_CREDENTIALS and GOOGLE_PROJECT_ID."],
		claudeCli:
			"claude mcp add analytics-mcp --env GOOGLE_APPLICATION_CREDENTIALS=PATH_TO_CREDENTIALS_JSON --env GOOGLE_PROJECT_ID=YOUR_PROJECT_ID -- pipx run analytics-mcp",
		alternate: "Install from Google source and configure manually.",
		references: ["https://developers.google.com/analytics/devguides/MCP"]
	},
	shopify: {
		category: "Commerce MCP",
		auth: "Shopify Dev MCP generally requires no token for documentation and developer guidance.",
		chatgptWeb: [
			"Use Shopify plugin/connector where available for ChatGPT.",
			"For local developer MCP, use CLI/app setup instead."
		],
		claudeApp: [
			"Configure the stdio server in Claude Desktop or install the Shopify AI toolkit plugin where supported."
		],
		claudeCli: "claude mcp add --transport stdio shopify-dev-mcp -- npx -y @shopify/dev-mcp@latest",
		alternate: "claude plugin install shopify-ai-toolkit@claude-plugins-official",
		references: [
			"https://shopify.dev/docs/apps/build/storefront-mcp",
			"https://shopify.dev/docs/apps/build/ai-toolkit",
			"https://github.com/openai/plugins/blob/main/plugins/shopify/.codex-plugin/plugin.json"
		]
	},
	"email-on-acid": {
		category: "Email QA MCP",
		auth: "Pipedream connected account / OAuth.",
		chatgptWeb: ["Use Pipedream MCP app endpoint in a remote MCP connector where supported."],
		claudeApp: ["Add the Pipedream MCP app endpoint as a remote MCP."],
		claudeCli: "Use the Pipedream MCP URL for the Email on Acid app from the linked page.",
		alternate: "Store API credentials in Pipedream rather than in the local client.",
		references: ["https://mcp.pipedream.com/app/email_on_acid"]
	},
	rippling: {
		category: "HR MCP",
		auth: "Rippling API token from developer/admin settings, if enabled.",
		chatgptWeb: ["Use Bifrost or another hosted MCP gateway for web clients."],
		claudeApp: ["Configure local stdio or a hosted gateway endpoint."],
		claudeCli: "claude mcp add rippling --env RIPPLING_API_TOKEN=YOUR_TOKEN -- npx -y rippling-mcp-server",
		alternate: "Use Bifrost hosted MCP gateway if your org supports it.",
		references: ["https://mcpservers.org/servers/bifrost-mcp/rippling-mcp"]
	},
	quickbooks: {
		category: "Finance MCP",
		auth: "OAuth refresh token plus Intuit app credentials and realm ID.",
		chatgptWeb: ["Use a hosted MCP bridge; local refresh token setup is not suited for web-only clients."],
		claudeApp: ["Run the server locally, then add it through app stdio config."],
		claudeCli:
			"claude mcp add quickbooks --env QUICKBOOKS_CLIENT_ID=YOUR_CLIENT_ID --env QUICKBOOKS_CLIENT_SECRET=YOUR_CLIENT_SECRET --env QUICKBOOKS_REFRESH_TOKEN=YOUR_REFRESH_TOKEN --env QUICKBOOKS_REALM_ID=YOUR_REALM_ID --env QUICKBOOKS_ENVIRONMENT=production -- node /path/to/quickbooks-online-mcp-server/dist/index.js",
		alternate: "Run the server locally, then add it through stdio config.",
		references: ["https://github.com/intuit/quickbooks-online-mcp-server"]
	},
	aws: {
		category: "Cloud MCP",
		auth: "Uses AWS credentials from local environment/profile where required.",
		chatgptWeb: ["Use through a hosted MCP bridge only if credentials are managed server-side."],
		claudeApp: ["Configure one or more AWS Labs stdio servers with local AWS credentials."],
		claudeCli: "claude mcp add aws-api -- uvx awslabs.aws-api-mcp-server@latest",
		alternate:
			"claude mcp add aws-docs -- uvx awslabs.aws-documentation-mcp-server@latest\nclaude mcp add aws-pricing -- uvx awslabs.aws-pricing-mcp-server@latest",
		references: ["https://github.com/awslabs/mcp"]
	},
	wingman: {
		category: "Local Automation MCP",
		auth: "Depends on local Wingman setup.",
		chatgptWeb: ["Not available to web clients unless Wingman is exposed through an approved HTTPS bridge."],
		claudeApp: ["Run Wingman locally and connect the app to its local MCP endpoint."],
		claudeCli: "claude mcp add --transport http wingman http://localhost:8080/v1/mcp/wingman",
		alternate: "Use local Wingman config/admin UI to manage auth.",
		references: ["https://github.com/adrianliechti/wingman"]
	},
	linode: {
		category: "Cloud MCP",
		auth: "Linode API token from Cloud Manager -> My Profile -> API Tokens.",
		chatgptWeb: ["Use a hosted MCP bridge if token storage is managed server-side."],
		claudeApp: ["Configure local stdio with token in the MCP environment."],
		claudeCli: "claude mcp add linode -- npx -y @takashito/linode-mcp-server --token YOUR_LINODE_API_TOKEN",
		alternate: "Set token as an environment variable if supported by the server.",
		references: ["https://github.com/takashito/linode-mcp-server"]
	},
	zoom: {
		category: "Meeting MCP",
		auth: "Zoom OAuth or plugin authorization.",
		chatgptWeb: ["Use the Zoom plugin/connector in ChatGPT where available."],
		claudeApp: ["Use Zoom MCP app setup if available in your client."],
		claudeCli: "Use Zoom MCP server setup from Zoom docs; command varies by client.",
		alternate: "OpenAI Zoom plugin reference is linked below.",
		references: ["https://developers.zoom.us/docs/mcp/", "https://github.com/openai/plugins/tree/main/plugins/zoom"]
	},
	salesloft: {
		category: "Sales Engagement MCP",
		auth: "Salesloft OAuth or account token per Salesloft MCP setup.",
		chatgptWeb: ["Use a remote MCP connector if your workspace supports Salesloft MCP."],
		claudeApp: ["Add Salesloft MCP through supported client connector setup."],
		claudeCli: "Use the Salesloft MCP server setup from Salesloft docs.",
		alternate: "Keep Salesloft credentials in the approved connector setup.",
		references: ["https://help.salesloft.com/s/article/Salesloft-MCP-Server"]
	},
	linkedin: {
		category: "Research MCP",
		auth: "Depends on the community MCP server configuration.",
		chatgptWeb: ["Use only through a reviewed hosted MCP bridge; verify data policy first."],
		claudeApp: ["Configure local stdio per the community server README."],
		claudeCli: "Use the linked linkedin-mcp-server README for current command and auth flags.",
		alternate: "Treat as experimental until security review.",
		references: ["https://github.com/stickerdaniel/linkedin-mcp-server"]
	},
	"adobe-express": {
		category: "Design MCP",
		auth: "Adobe local development/auth setup for Express add-ons.",
		chatgptWeb: ["Not relevant for web-only ChatGPT unless exposed through a hosted bridge."],
		claudeApp: ["Run the local development MCP server and connect via app config."],
		claudeCli: "Use Adobe Express local development MCP instructions from the linked docs.",
		alternate: "Keep add-on local development scoped to dev environments.",
		references: [
			"https://developer.adobe.com/express/add-ons/docs/guides/getting-started/local-development/mcp-server"
		]
	},
	excalidraw: {
		category: "Diagram MCP",
		auth: "Excalidraw Plus account authorization.",
		chatgptWeb: ["Use remote MCP connector where Excalidraw Plus supports it."],
		claudeApp: ["Add Excalidraw MCP endpoint from Plus docs."],
		claudeCli: "Use the Excalidraw Plus MCP endpoint and auth from the linked docs.",
		alternate: "Use FigJam diagram generation for internal AOP diagrams when MCP is unavailable.",
		references: ["https://plus.excalidraw.com/docs/mcp"]
	}
};

const OPENAI_PLUGIN_REFERENCES = [
	["ZoomInfo", "https://github.com/openai/plugins/blob/main/plugins/zoominfo/.codex-plugin/plugin.json"],
	["Similarweb", "https://github.com/openai/plugins/blob/main/plugins/similarweb/.codex-plugin/plugin.json"],
	["Sentry", "https://github.com/openai/plugins/blob/main/plugins/sentry/.codex-plugin/plugin.json"],
	[
		"Particl Market Research",
		"https://github.com/openai/plugins/blob/main/plugins/particl-market-research/.codex-plugin/plugin.json"
	],
	["Build Web Apps", "https://github.com/openai/plugins/tree/main/plugins/build-web-apps"],
	["Build Web Data Visualization", "https://github.com/openai/plugins/tree/main/plugins/build-web-data-visualization"]
];

const MCP_SERVERS = [...BASE_MCP_SERVERS, ...EXTRA_MCP_SERVERS].map((server) => ({
	...server,
	install: INSTALL_GUIDES[server.id] || null
}));

const STATUS_COLORS = { connected: "#34c759", "auth-required": "#f97316", disconnected: "#ff625d" };
const STATUS_LABELS = { connected: "Connected", "auth-required": "Auth Required", disconnected: "Disconnected" };

const INSPECTOR_PROFILES = [
	{
		id: "stdio",
		label: "Local stdio",
		command: "pnpm mcp:inspect",
		target: "tsx src/mcp/internalDocsServer.ts",
		endpoint: "stdio transport",
		auth: "none",
		note: "Uses the root package shortcut for the internal MCP server."
	},
	{
		id: "server-stdio",
		label: "Server package",
		command: "pnpm --dir server mcp:inspect",
		target: "src/mcp/internalDocsServer.ts",
		endpoint: "stdio transport",
		auth: "none",
		note: "Same inspector flow when working directly from the server package."
	},
	{
		id: "http",
		label: "HTTP connector",
		command: "INTERNAL_MCP_BEARER_TOKEN=replace-me pnpm mcp:http",
		target: "Streamable HTTP",
		endpoint: "http://127.0.0.1:3333/mcp",
		auth: "bearer token",
		note: "Use with a short-lived HTTPS tunnel when external clients cannot use stdio."
	}
];

function buildDefaultInput(tool) {
	const defaults = {};
	Object.entries(tool.inputSchema).forEach(([k, v]) => {
		if (v.required) defaults[k] = v.type === "number" ? 5 : "";
	});
	return JSON.stringify(defaults, null, 2);
}

function SchemaRow({ name, field }) {
	return (
		<div
			style={{
				display: "flex",
				gap: 8,
				alignItems: "baseline",
				padding: "4px 0",
				borderBottom: "1px solid var(--border)",
				fontSize: 12
			}}
		>
			<code style={{ color: "var(--blue)", minWidth: 130, fontFamily: "monospace" }}>{name}</code>
			<span style={{ color: "var(--text-dim)", fontSize: 11, minWidth: 60 }}>{field.type}</span>
			{field.required && <span style={{ color: "var(--orange)", fontSize: 10, fontWeight: 600 }}>required</span>}
			<span style={{ color: "var(--text-sec)", flex: 1 }}>{field.description}</span>
		</div>
	);
}

function InstallSteps({ title, steps }) {
	if (!steps) return null;
	const normalized = Array.isArray(steps) ? steps : [steps];
	const commandLike = normalized.length === 1 && normalized[0].includes("claude mcp add");

	return (
		<div className="aop-mcp-install-block">
			<div className="aop-mcp-install-title">{title}</div>
			{commandLike ? (
				<pre className="aop-mcp-install-command">{normalized[0]}</pre>
			) : (
				<ol className="aop-mcp-install-steps">
					{normalized.map((step) => (
						<li key={step}>{step}</li>
					))}
				</ol>
			)}
		</div>
	);
}

function InstallGuide({ server }) {
	const guide = server.install;
	if (!guide) {
		return (
			<div className="aop-card" style={{ marginBottom: 16 }}>
				<div className="aop-card-header">
					<span className="aop-card-title">Install Guide</span>
				</div>
				<div className="aop-card-body">
					<p className="aop-mcp-inspector-note">No setup guide has been added for this server yet.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="aop-card aop-mcp-install-card">
			<div className="aop-card-header">
				<div>
					<span className="aop-card-title">Install Guide</span>
					<div className="aop-card-subtitle">{guide.category}</div>
				</div>
				{/* <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<span className="aop-badge queued">{server.transport}</span>
				</div> */}
			</div>
			<div className="aop-card-body">
				{guide.auth && (
					<div className="aop-mcp-install-auth">
						<span>Auth</span>
						<p>{guide.auth}</p>
					</div>
				)}
				<div className="aop-mcp-install-grid">
					<InstallSteps title="ChatGPT Web" steps={guide.chatgptWeb} />
					<InstallSteps title="Claude App" steps={guide.claudeApp} />
					<InstallSteps title="Claude CLI" steps={guide.claudeCli} />
				</div>
				{guide.alternate && (
					<div className="aop-mcp-install-alt">
						<div className="aop-mcp-install-title">Alternate Setup</div>
						<pre className="aop-mcp-install-command">{guide.alternate}</pre>
					</div>
				)}
				{guide.references?.length > 0 && (
					<div className="aop-mcp-reference-list">
						{guide.references.map((ref) => (
							<a key={ref} href={ref} target="_blank" rel="noreferrer">
								{ref}
							</a>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

function PluginReferencePanel() {
	return (
		<div className="aop-card aop-mcp-plugin-card">
			<div className="aop-card-header">
				<div>
					<span className="aop-card-title">ChatGPT Plugin References</span>
					<div className="aop-card-subtitle">
						Connector/plugin examples useful when mapping web/app setup patterns
					</div>
				</div>
			</div>
			<div className="aop-card-body">
				<div className="aop-mcp-plugin-list">
					{OPENAI_PLUGIN_REFERENCES.map(([label, href]) => (
						<a key={href} href={href} target="_blank" rel="noreferrer">
							{label}
						</a>
					))}
				</div>
			</div>
		</div>
	);
}

function InspectorStat({ label, value, tone }) {
	return (
		<div className="aop-mcp-inspector-stat">
			<div className="aop-mcp-inspector-stat-value" style={tone ? { color: tone } : undefined}>
				{value}
			</div>
			<div className="aop-mcp-inspector-stat-label">{label}</div>
		</div>
	);
}

function McpInspector({ selectedServer, bridge, onBridgeUpdate }) {
	const [profileId, setProfileId] = useState("stdio");
	const [running, setRunning] = useState(false);
	const [lastRun, setLastRun] = useState(null);
	const profile = INSPECTOR_PROFILES.find((p) => p.id === profileId) || INSPECTOR_PROFILES[0];
	const isLive = selectedServer.id === LIVE_SERVER_ID;

	// Real handshake via the bridge: connect, initialize, tools/list against
	// the running mcp:http process.
	const handleRunInspector = async () => {
		setRunning(true);
		setLastRun(null);
		try {
			const res = await mcpApi.status();
			if (!res.success) throw new Error(res.error || "Bridge request failed");
			const b = res.bridge;
			if (onBridgeUpdate) onBridgeUpdate(b);
			setLastRun({
				ok: b.available,
				bridge: b,
				logs: [
					`connect: ${b.url}`,
					b.available
						? `initialize: ${b.serverInfo ? `${b.serverInfo.name} v${b.serverInfo.version}` : "ok"} (${b.latencyMs}ms)`
						: `error: ${b.error}`,
					b.available ? `tools/list: ${b.toolCount} tools discovered` : "handshake failed — is `pnpm mcp:http` running?",
					`write tools: ${b.writesEnabled ? "ENABLED (MCP_BRIDGE_ALLOW_WRITES=1)" : "blocked (bridge is read-only)"}`
				]
			});
		} catch (err) {
			setLastRun({ ok: false, bridge: null, logs: [`error: ${err.message}`] });
		} finally {
			setRunning(false);
		}
	};

	const bridgeState = (lastRun && lastRun.bridge) || bridge;

	return (
		<div className="aop-card aop-mcp-inspector-card">
			<div className="aop-card-header">
				<div>
					<span className="aop-card-title">MCP Inspector</span>
					<div className="aop-card-subtitle">
						{isLive
							? "Live handshake and tool discovery via the dashboard bridge"
							: "Run the MCP Inspector locally for external servers"}
					</div>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<a
						className="aop-mcp-doc-link"
						href="https://modelcontextprotocol.io/docs/tools/inspector"
						target="_blank"
						rel="noreferrer"
					>
						Docs
					</a>
					{isLive ? (
						<span className={`aop-badge ${bridgeState && bridgeState.available ? "completed" : "pending"}`}>
							{bridgeState && bridgeState.available ? "Connected" : "Offline"}
						</span>
					) : (
						<span className="aop-badge" style={{ background: "rgba(156,163,197,0.12)", color: "var(--text-sec)" }}>
							Local only
						</span>
					)}
				</div>
			</div>
			<div className="aop-card-body">
				{isLive ? (
					<div className="aop-mcp-inspector-grid">
						<div>
							<div className="aop-form-group">
								<label className="aop-form-label">Inspector Profile</label>
								<select
									className="aop-form-select"
									value={profileId}
									onChange={(e) => setProfileId(e.target.value)}
								>
									{INSPECTOR_PROFILES.map((p) => (
										<option key={p.id} value={p.id}>
											{p.label}
										</option>
									))}
								</select>
							</div>

							<div className="aop-mcp-command-block">
								<div className="aop-mcp-command-label">Command</div>
								<code>{profile.command}</code>
							</div>

							<div className="aop-mcp-inspector-meta">
								<div>
									<span>Target</span>
									<code>{profile.target}</code>
								</div>
								<div>
									<span>Endpoint</span>
									<code>{profile.endpoint}</code>
								</div>
								<div>
									<span>Auth</span>
									<code>{profile.auth}</code>
								</div>
							</div>

							<p className="aop-mcp-inspector-note">{profile.note}</p>
							<button className="aop-btn trigger" onClick={handleRunInspector} disabled={running}>
								{running ? "Inspecting..." : "Run Live Handshake"}
							</button>
						</div>

						<div>
							<div className="aop-mcp-inspector-stats">
								<InspectorStat label="Server" value={selectedServer.name} />
								<InspectorStat
									label="Tools"
									value={bridgeState && bridgeState.available ? bridgeState.toolCount : "—"}
									tone="var(--blue)"
								/>
								<InspectorStat
									label="Latency"
									value={bridgeState && bridgeState.latencyMs != null ? `${bridgeState.latencyMs}ms` : "—"}
								/>
							</div>

							<div className="aop-mcp-checklist">
								{[
									["bridge reachable", Boolean(bridgeState && bridgeState.available)],
									["tools discovered", Boolean(bridgeState && bridgeState.toolCount > 0)],
									["write tools blocked", Boolean(bridgeState && !bridgeState.writesEnabled)]
								].map(([label, passed]) => (
									<div key={label} className="aop-mcp-check-row">
										<span className={`aop-mcp-check-dot${passed ? " passed" : " warning"}`} />
										<span>{label}</span>
										<strong>{passed ? "pass" : "review"}</strong>
									</div>
								))}
							</div>

							{lastRun && <pre className="aop-mcp-inspector-log">{lastRun.logs.join("\n")}</pre>}
						</div>
					</div>
				) : (
					<div>
						<p className="aop-mcp-inspector-note" style={{ marginTop: 0 }}>
							{selectedServer.name} runs as a {selectedServer.transport} server outside this app, so the
							dashboard cannot connect to it. Inspect it from your machine with the official MCP
							Inspector:
						</p>
						<div className="aop-mcp-command-block">
							<div className="aop-mcp-command-label">Command</div>
							<code>npx @modelcontextprotocol/inspector</code>
						</div>
						<p className="aop-mcp-inspector-note">
							Then add this server with the connection details from its install guide above.
						</p>
					</div>
				)}
			</div>
		</div>
	);
}

export default function McpTools() {
	const [selectedServerId, setSelectedServerId] = useState(MCP_SERVERS[0].id);
	const [selectedToolName, setSelectedToolName] = useState(MCP_SERVERS[0].tools[0].name);
	const [testInput, setTestInput] = useState(() => buildDefaultInput(MCP_SERVERS[0].tools[0]));
	const [testOutput, setTestOutput] = useState(null);
	const [testing, setTesting] = useState(false);
	const [bridge, setBridge] = useState(null);
	const [liveTools, setLiveTools] = useState(null);

	// Discover the internal server through the bridge: live status + real
	// tool list with schemas. Offline is a rendered state, not an error.
	useEffect(() => {
		let cancelled = false;
		mcpApi
			.status()
			.then((res) => {
				if (cancelled || !res.success) return null;
				setBridge(res.bridge);
				return res.bridge.available ? mcpApi.tools() : null;
			})
			.then((res) => {
				if (!cancelled && res && res.success) setLiveTools(res);
			})
			.catch(() => {
				if (!cancelled) setBridge({ available: false, error: "bridge unreachable" });
			});
		return () => {
			cancelled = true;
		};
	}, []);

	// Catalog with the internal server's entry replaced by live data when the
	// bridge is up. External servers stay as documentation.
	const servers = useMemo(
		() =>
			MCP_SERVERS.map((server) => {
				if (server.id !== LIVE_SERVER_ID) return server;
				const status = bridge == null ? server.status : bridge.available ? "connected" : "disconnected";
				const tools = liveTools ? liveTools.tools.map(liveToolToCatalog) : server.tools;
				return { ...server, status, tools, transport: "Streamable HTTP via bridge" };
			}),
		[bridge, liveTools]
	);

	const selectedServer = servers.find((s) => s.id === selectedServerId) || servers[0];
	const selectedTool =
		selectedServer.tools.find((t) => t.name === selectedToolName) || selectedServer.tools[0] || null;

	const isLiveServer = selectedServer.id === LIVE_SERVER_ID && Boolean(bridge && bridge.available);
	const writesEnabled = Boolean(liveTools && liveTools.writesEnabled);
	const writeBlocked = isLiveServer && selectedTool && selectedTool.readOnly === false && !writesEnabled;

	const handleServerSelect = (server) => {
		setSelectedServerId(server.id);
		setSelectedToolName(server.tools[0] && server.tools[0].name);
		if (server.tools[0]) setTestInput(buildDefaultInput(server.tools[0]));
		setTestOutput(null);
	};

	const handleToolSelect = (tool) => {
		setSelectedToolName(tool.name);
		setTestInput(buildDefaultInput(tool));
		setTestOutput(null);
	};

	// Real tool call through the bridge — internal server only.
	const handleTest = async () => {
		if (!isLiveServer || !selectedTool) return;
		let args = {};
		if (testInput.trim()) {
			try {
				args = JSON.parse(testInput);
			} catch {
				setTestOutput({ error: "Input is not valid JSON." });
				return;
			}
		}
		setTesting(true);
		setTestOutput(null);
		try {
			const res = await mcpApi.call(selectedTool.name, args);
			if (!res.success) throw new Error(res.error || "Tool call failed");
			const text = (res.content || [])
				.map((c) => (c.type === "text" ? c.text : JSON.stringify(c)))
				.join("\n");
			setTestOutput({
				latency: `${res.latencyMs}ms`,
				isError: res.isError,
				result: prettyMaybeJson(text || JSON.stringify(res.structuredContent || {}, null, 2))
			});
		} catch (err) {
			setTestOutput({ error: err.message });
		} finally {
			setTesting(false);
		}
	};

	return (
		<div>
			<div className="aop-page-header">
				<div>
					<h1 className="aop-page-title">MCP Tools</h1>
					<p className="aop-page-subtitle">Inspect and test Model Context Protocol server tools</p>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					{bridge == null ? (
						<span className="aop-badge" style={{ background: "rgba(156,163,197,0.12)", color: "var(--text-sec)" }}>
							Checking bridge…
						</span>
					) : bridge.available ? (
						<span className="aop-badge completed">
							CSD Internal connected{liveTools ? ` · ${liveTools.tools.length} tools live` : ""}
						</span>
					) : (
						<span className="aop-badge pending">CSD Internal offline</span>
					)}
				</div>
			</div>

			<InstallGuide server={selectedServer} />

			<div className="aop-mcp-layout">
				{/* Server list */}
				<div className="aop-mcp-servers">
					<div
						style={{
							fontSize: 10,
							fontWeight: 700,
							color: "var(--text-dim)",
							textTransform: "uppercase",
							letterSpacing: "0.08em",
							padding: "0 0 8px"
						}}
					>
						Servers
					</div>
					{servers.map((server) => (
						<div
							key={server.id}
							className={`aop-mcp-server-item${selectedServer.id === server.id ? " active" : ""}`}
							onClick={() => handleServerSelect(server)}
						>
							<span style={{ fontSize: 16, marginRight: 8, flexShrink: 0 }}>{server.icon}</span>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{server.name}</div>
								{server.id === LIVE_SERVER_ID ? (
									<div style={{ fontSize: 10, color: STATUS_COLORS[server.status], marginTop: 1 }}>
										● {STATUS_LABELS[server.status]}
									</div>
								) : (
									<div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 1 }}>● Docs only</div>
								)}
							</div>
							<span
								style={{
									fontSize: 10,
									color: "var(--text-dim)",
									background: "var(--pill)",
									padding: "2px 6px",
									borderRadius: 4,
									flexShrink: 0
								}}
							>
								{server.tools.length}
							</span>
						</div>
					))}
				</div>

				{/* Tool list */}
				<div className="aop-mcp-tools-list">
					<div style={{ padding: "0 0 8px", borderBottom: "1px solid var(--border)", marginBottom: 8 }}>
						<div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{selectedServer.name}</div>
						<div style={{ fontSize: 11, color: "var(--text-sec)", marginTop: 3 }}>
							{selectedServer.transport} · {selectedServer.tools.length} tools
						</div>
					</div>
					<div style={{ fontSize: 11, color: "var(--text-sec)", marginBottom: 12, lineHeight: 1.5 }}>
						{selectedServer.description}
					</div>
					{selectedServer.tools.map((tool) => (
						<div
							key={tool.name}
							className={`aop-mcp-tool-item${selectedTool?.name === tool.name ? " active" : ""}`}
							onClick={() => handleToolSelect(tool)}
						>
							<code
								style={{
									fontSize: 12,
									color: selectedTool?.name === tool.name ? "var(--blue)" : "var(--text)",
									fontFamily: "monospace",
									display: "block",
									marginBottom: 2
								}}
							>
								{tool.name}
							</code>
							<div style={{ fontSize: 11, color: "var(--text-dim)", lineHeight: 1.4 }}>
								{tool.description}
							</div>
						</div>
					))}
				</div>

				{/* Tool detail + tester */}
				{selectedTool && (
					<div className="aop-mcp-detail">
						<div style={{ marginBottom: 20 }}>
							<code
								style={{ fontSize: 16, fontWeight: 700, color: "var(--blue)", fontFamily: "monospace" }}
							>
								{selectedTool.name}
							</code>
							<p style={{ fontSize: 13, color: "var(--text-sec)", marginTop: 6, lineHeight: 1.6 }}>
								{selectedTool.description}
							</p>
						</div>

						{Object.keys(selectedTool.inputSchema).length > 0 && (
							<div className="aop-card" style={{ marginBottom: 16 }}>
								<div className="aop-card-header">
									<span className="aop-card-title">Input Schema</span>
								</div>
								<div className="aop-card-body">
									{Object.entries(selectedTool.inputSchema).map(([k, v]) => (
										<SchemaRow key={k} name={k} field={v} />
									))}
								</div>
							</div>
						)}

						<div className="aop-card" style={{ marginBottom: 16 }}>
							<div className="aop-card-header">
								<span className="aop-card-title">Output Shape</span>
							</div>
							<div className="aop-card-body">
								<pre
									style={{
										margin: 0,
										fontSize: 12,
										color: "var(--text-sec)",
										fontFamily: "monospace",
										lineHeight: 1.5
									}}
								>
									{selectedTool.outputShape}
								</pre>
							</div>
						</div>

						<div className="aop-card">
							<div className="aop-card-header">
								<span className="aop-card-title">Test Execution</span>
								<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
									{isLiveServer ? (
										<span className="aop-badge completed">Live</span>
									) : selectedServer.id === LIVE_SERVER_ID ? (
										<span className="aop-badge pending">Offline</span>
									) : (
										<span
											className="aop-badge"
											style={{ background: "rgba(156,163,197,0.12)", color: "var(--text-sec)" }}
										>
											Docs only
										</span>
									)}
								</div>
							</div>
							<div className="aop-card-body">
								{selectedServer.id !== LIVE_SERVER_ID ? (
									<p className="aop-mcp-inspector-note" style={{ marginTop: 0 }}>
										Live execution is only available for the CSD Internal server, which this app can
										reach through its bridge. {selectedServer.name} runs outside this app — use the
										MCP Inspector locally (see below) or the install guide above.
									</p>
								) : !isLiveServer ? (
									<p className="aop-mcp-inspector-note" style={{ marginTop: 0 }}>
										The internal MCP server is not reachable
										{bridge && bridge.error ? ` (${bridge.error})` : ""}. Start it with{" "}
										<code style={{ color: "var(--orange)" }}>
											INTERNAL_MCP_BEARER_TOKEN=… pnpm mcp:http
										</code>{" "}
										and reload this page.
									</p>
								) : (
									<>
										{writeBlocked && (
											<p className="aop-mcp-inspector-note" style={{ marginTop: 0, color: "var(--orange)" }}>
												<code>{selectedTool.name}</code> is a write tool and the bridge is
												read-only. Set <code>MCP_BRIDGE_ALLOW_WRITES=1</code> on the server to
												enable write execution.
											</p>
										)}
										<div className="aop-form-group">
											<label className="aop-form-label">Input JSON</label>
											<textarea
												className="aop-form-input"
												style={{
													fontFamily: "monospace",
													fontSize: 12,
													minHeight: 90,
													resize: "vertical"
												}}
												value={testInput}
												onChange={(e) => setTestInput(e.target.value)}
											/>
										</div>
										<button
											className="aop-btn trigger"
											style={{ marginBottom: 12 }}
											onClick={handleTest}
											disabled={testing || writeBlocked}
										>
											{testing ? "Running…" : "▶  Execute Tool"}
										</button>
										{testOutput && (
											<div
												style={{
													background: "var(--bg)",
													border: "1px solid var(--border)",
													borderRadius: 8,
													padding: 12
												}}
											>
												<div style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 11 }}>
													{testOutput.error ? (
														<span className="aop-badge failed">Error</span>
													) : testOutput.isError ? (
														<span className="aop-badge failed">Tool Error</span>
													) : (
														<span className="aop-badge completed">OK</span>
													)}
													{testOutput.latency && (
														<span style={{ color: "var(--text-dim)" }}>{testOutput.latency}</span>
													)}
													<span style={{ color: "var(--green)", marginLeft: "auto" }}>
														live response
													</span>
												</div>
												<pre
													style={{
														margin: 0,
														fontSize: 11,
														color: testOutput.error || testOutput.isError ? "var(--red)" : "var(--green)",
														fontFamily: "monospace",
														lineHeight: 1.5,
														whiteSpace: "pre-wrap",
														maxHeight: 360,
														overflow: "auto"
													}}
												>
													{testOutput.error || testOutput.result}
												</pre>
											</div>
										)}
									</>
								)}
							</div>
						</div>

						<McpInspector selectedServer={selectedServer} bridge={bridge} onBridgeUpdate={setBridge} />
						<PluginReferencePanel />
					</div>
				)}
			</div>
		</div>
	);
}
